import clsx from "clsx"
import { useTranslation } from "react-i18next"
import styles from "./ProfileBlock.module.scss"
import { StarIcon } from "../../icons/StarIcon"
import CropModal from "../CropModal/CropModal"
import { GetUserAvatar } from "../Assets/GetUserAvatar/GetUserAvatar"
import { useAppDispatch, useAppSelector } from "../../hooks"
import { IEmployee, IProfile } from "../../types/content"
import { ChatIcon } from "../../icons/ChatIcon"
import { useCreateChatMutation, useGetUserContactsMutation } from "../../redux/api/chat"
import { setMessagesModalFirstChat, setMessagesModalIsOpen } from "../../redux/slice/modals"
import BtnLoader from "../Assets/BtnLoader/BtnLoader"
import { addChatToTop } from "../../redux/slice/chat"
import { selectUser } from "../../redux/slice/auth"
import { useEffect, useState } from "react"

interface Props {
  className?: string
  profileData: IProfile | IEmployee
  isProfilePage?: boolean
  setImg?: (file64: string) => void
  delPhotoFcn?: () => void
  notViewPhones?: boolean
  isWriteMes?: boolean
  isLoadingAvatar?: boolean
  projectID?: string
}

const ProfileBlock = ({
  className,
  profileData,
  isProfilePage,
  setImg,
  delPhotoFcn,
  notViewPhones,
  isWriteMes,
  isLoadingAvatar,
  projectID,
}: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const dispatch = useAppDispatch()
  const [createChat, { isLoading: isLoadingCreateChat }] = useCreateChatMutation()
  const [getUserContacts, { isLoading: isLoadingUserContacts }] = useGetUserContactsMutation()
  const [contactsData, setContactsData] = useState<any>(null)

  useEffect(() => {
    getUserContacts({ id: profileData.id })
      .unwrap()
      .then((result: any) => {
        setContactsData(result)
      })
      .catch((err: any) => {
        console.log(err)
      })
  }, [])

  const user = useAppSelector(selectUser)

  const handleOpenChat = () => {
    createChat({ type: "users", id: profileData.id, project_id: projectID })
      .unwrap()
      .then((res) => {
        dispatch(setMessagesModalFirstChat(res))
        dispatch(setMessagesModalIsOpen(true))
        dispatch(addChatToTop({ ...res, messages: [], isMessagesLoaded: false, isVisible: true }))
      })
  }

  return (
    <div className={clsx(styles.ProfileBlock, className)}>
      <div className={styles["fio-container"]}>
        <span className={styles["fio"]}>{profileData.name}</span>
      </div>
      <CropModal
        addClass={styles["ProfileBlock__cropModal"]}
        submitModal={setImg}
        blockType={"profile"}
        delPhotoFcn={delPhotoFcn}
        photo={isProfilePage ? user.avatar_id : profileData?.avatar_id}
        isLoading={isLoadingAvatar}
      />
      <div className={styles["info-list"]}>
        {profileData.position && (
          <div className={styles["info-list__item"]}>
            <span className={styles["item__title"]}>{t("position")}</span>
            <span className={styles["item__info"]}>{profileData.position}</span>
          </div>
        )}
        {profileData.department_name && (
          <div className={styles["info-list__item"]}>
            <span className={styles["item__title"]}>{t("department")}</span>
            <span className={styles["item__info"]}>{profileData.department_name}</span>
          </div>
        )}

        {profileData.manager && (
          <div className={styles["info-list__item"]}>
            <span className={styles["item__title"]}>{t("leader")}</span>
            <div className={styles["item__info-container"]}>
              {profileData.manager.avatar_id && (
                <GetUserAvatar avatar={profileData.manager.avatar_id} name={profileData.manager.name} size={"sm"} />
              )}

              <span className={styles["item__info"]}>{profileData.manager.name}</span>
            </div>
          </div>
        )}
        {contactsData?.email && (
          <div className={styles["info-list__item"]}>
            <span className={styles["item__title"]}>Email</span>
            <span className={styles["item__info"]}>{contactsData?.email}</span>
          </div>
        )}
        {!notViewPhones && (
          <>
            {!!(contactsData?.phone_number || contactsData?.phoneNumber) && (
              <div className={styles["info-list__item"]}>
                <span className={styles["item__title"]}>{t("mainPhone")}</span>
                <span className={styles["item__info"]}>{contactsData?.phone_number ?? contactsData?.phoneNumber}</span>
              </div>
            )}
            {!!(contactsData?.add_phone_number || contactsData?.addPhoneNumber) && (
              <div className={styles["info-list__item"]}>
                <span className={styles["item__title"]}>{t("additionalPhone")}</span>
                <span className={styles["item__info"]}>
                  {contactsData?.add_phone_number ?? contactsData?.addPhoneNumber}
                </span>
              </div>
            )}
          </>
        )}
      </div>
      {isProfilePage && <div className={styles["additional-information"]}>{t("txtChangeInfoSupport")}</div>}
      {isWriteMes && (
        <div className={styles["write-message__block"]}>
          <button
            disabled={isLoadingCreateChat}
            className={clsx(styles.btn, isLoadingCreateChat && styles["btn--is-loading"])}
            onClick={handleOpenChat}
          >
            <div className={styles["btn__icon-container"]}>
              {isLoadingCreateChat && <BtnLoader />}
              <ChatIcon />
            </div>

            {t("write")}
          </button>
        </div>
      )}
    </div>
  )
}

export default ProfileBlock
